import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Alert.
 *
 * Four tones — default (neutral), info (blue), warning (yellow),
 * destructive (red). Each follows the "subtle bg + matching
 * border + matching foreground" pattern from CF's alert chrome.
 * No drop shadow, slim border, tight padding.
 *
 * Use `<Alert>` for the container, with arbitrary children inside —
 * pair with `<AlertTitle>` and `<AlertDescription>` for the
 * canonical structure, or freestyle if your message is one line.
 */
const alertVariants = cva(
  'relative w-full rounded-md border px-3 py-2.5 text-xs [&>svg]:absolute [&>svg]:left-3 [&>svg]:top-3 [&>svg+div]:translate-y-[-1px] [&>svg]:size-3.5 [&>svg~*]:pl-6',
  {
    variants: {
      tone: {
        default:
          'border-neutral-800 bg-neutral-900/50 text-foreground [&>svg]:text-muted-foreground',
        info: 'border-blue-900/60 bg-blue-950/30 text-blue-300 [&>svg]:text-blue-400',
        warning:
          'border-yellow-900/60 bg-yellow-950/20 text-yellow-300 [&>svg]:text-yellow-400',
        destructive:
          'border-red-900/60 bg-red-950/30 text-red-300 [&>svg]:text-red-400',
      },
    },
    defaultVariants: { tone: 'default' },
  },
);

export interface AlertProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ className, tone, ...props }, ref) => (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ tone }), className)}
      {...props}
    />
  ),
);
Alert.displayName = 'Alert';

export const AlertTitle = forwardRef<
  HTMLHeadingElement,
  HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn(
      'mb-0.5 text-xs font-semibold leading-tight tracking-tight',
      className,
    )}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

export const AlertDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-xs leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';
